// @ts-nocheck
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { HotelContext } from "../../context/HotelContext";
import { translate } from "../../utils/Translation";
import MonthYearNavigator from "./MonthYearNavigator";
import "./Page.css";
import "./RewardsList.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useV2MonthYear } from "../../hooks/useV2MonthYear";
import logoSlider from "../../img/v2/Logo_slider.png";
import logoDon from "../../img/v2/Logo_don.png";
import logoBoutique from "../../img/v2/Logo_boutique.png";
import linkIcon from "../../img/link.png";
import RewardsTopCards from "./RewardsTopCards";
import RewardsAnalysisTable from "./RewardsAnalysisTable";
import RewardsBenefitsTable from "./RewardsBenefitsTable";
import RewardsDetailsModal from "./RewardsDetailsModal";

const FRONT_TOKEN = "Bs#PSQyhy)!y9Ti{";

const formatCompactNumber = (value) => {
  if (value === null || value === undefined) return "--";
  if (typeof value !== "number") return value;
  if (value >= 1000000) return `${(value / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
  return value.toString();
};

const mapRewardTypeLabel = (rwType, lang) => {
  switch (rwType) {
    case "SLIDER":
      return translate("v2_rewards_type_slider", lang);
    case "CODE":
      return translate("v2_rewards_type_code", lang);
    case "EAU":
      return translate("v2_rewards_type_don_eau", lang);
    default:
      return rwType || "--";
  }
};

const mapRewardTypeBadgeClass = (rwType) => {
  switch (rwType) {
    case "SLIDER":
      return "rewards-list__type-badge--slider";
    case "CODE":
      return "rewards-list__type-badge--code";
    case "EAU":
      return "rewards-list__type-badge--water";
    default:
      return "rewards-list__type-badge--default";
  }
};

const getRewardIconColor = (rewardName, rwType) => {
  const name = (rewardName || "").toLowerCase();
  if (name.includes("spa")) return "#91B0D8";
  if (name.includes("boutique") || name.includes("shop")) return "#CFA7A0";
  if (rwType === "EAU") return "#6C8EBF";
  return "#B0C996";
};

const getRewardLogoSrc = (rwType) => {
  switch (rwType) {
    case "SLIDER":
      return logoSlider;
    case "EAU":
      return logoDon;
    case "CODE":
      return logoBoutique;
    default:
      return null;
  }
};

const RewardsList = () => {
  const auth = useAuth();
  const { hId, lang } = useContext(HotelContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { month: selectedMonth, year: selectedYear, setMonthYear } = useV2MonthYear();

  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [selectedReward, setSelectedReward] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchRewards = () => {
    if (!auth?.isAuthenticated || !auth.user?.access_token || !hId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasError(false);

    const params = new URLSearchParams({
      lang,
      month: String(selectedMonth),
      year: String(selectedYear),
    });
    const url = `/api/hotels/${hId}/rewards?${params.toString()}`;

    fetch(url, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + auth.user.access_token,
        "x-front-token": FRONT_TOKEN,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        setRewards(Array.isArray(data?.rewards) ? data.rewards : []);
        setHasError(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error("RewardsList - Error fetching rewards:", error);
        setHasError(true);
        setRewards([]);
        setLoading(false);
      });
  };

  const fetchRewardsStats = () => {
    if (!auth?.isAuthenticated || !auth.user?.access_token || !hId) {
      return;
    }

    setStatsLoading(true);
    const params = new URLSearchParams({
      lang,
      month: String(selectedMonth),
      year: String(selectedYear),
    });
    const url = `/api/hotels/${hId}/rewards/stats?${params.toString()}`;

    fetch(url, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + auth.user.access_token,
        "x-front-token": FRONT_TOKEN,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        setUsageStats(data);
        setStatsLoading(false);
      })
      .catch((error) => {
        console.error("RewardsList - Error fetching rewards stats:", error);
        setUsageStats(null);
        setStatsLoading(false);
      });
  };

  useEffect(() => {
    if (auth && auth.isAuthenticated && auth.user && hId) fetchRewards();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.isAuthenticated, hId, selectedMonth, selectedYear, lang]);

  useEffect(() => {
    if (auth && auth.isAuthenticated && auth.user && hId) fetchRewardsStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.isAuthenticated, hId, selectedMonth, selectedYear, lang]);

  const staysTotalForPeriod = useMemo(() => {
    const stays = usageStats?.meta?.staysTotal;
    return typeof stays === "number" ? stays : 0;
  }, [usageStats]);

  const averageUsageRate = useMemo(() => {
    const claimedPct = usageStats?.rates?.claimedPct;
    if (typeof claimedPct === "number") return claimedPct;
    return 0;
  }, [usageStats]);

  const averageUsageDelta = useMemo(() => {
    const delta = usageStats?.comparison?.deltaClaimedPct;
    return typeof delta === "number" ? delta : 0;
  }, [usageStats]);

  const usageChartData = useMemo(() => {
    if (usageStats?.success === "OK" && typeof usageStats.total === "number" && Array.isArray(usageStats.details)) {
      const claimedSeg = usageStats.details.find((d) => d?.type === "Récompenses réclamées");
      const obtainedSeg = usageStats.details.find((d) => d?.type === "Récompenses obtenues");
      const otherSegs = usageStats.details.filter((d) => d && d !== claimedSeg && d !== obtainedSeg);
      const orderedDetails = [claimedSeg, obtainedSeg, ...otherSegs].filter(Boolean);

      return {
        total: usageStats.total,
        details: orderedDetails.map((d, idx) => ({ ...d, actId: `seg-${idx}` })),
      };
    }
    return { total: 0, details: [] };
  }, [usageStats]);

  const formatUsageLegendValue = (value, segment, total) => {
    if (segment?.type === "Récompenses obtenues") {
      return `(${total || 0})`;
    }
    if (segment?.type === "Récompenses réclamées") {
      const pct = total > 0 ? Math.round((value / total) * 100) : 0;
      return `${pct}% (${value}/${total || 0})`;
    }
    return value?.toString?.() || "--";
  };

  const analysisRows = useMemo(() => {
    const denom = staysTotalForPeriod || 0;
    return rewards
      .slice()
      .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
      .map((r) => {
        const claimedTotal = r.usageCount || 0;
        const usedTotal = r.usedCount || 0;
        const claimedPct = denom > 0 ? Math.round((claimedTotal / denom) * 100) : 0;
        const usedPct = denom > 0 ? Math.round((usedTotal / denom) * 100) : 0;

        return {
          rwId: r.rwId,
          name: r.radName || translate("v2_rewards_unnamed", lang, { id: r.rwId }),
          rwType: r.rwType,
          claimPct: claimedPct,
          usePct: usedPct,
          description: r.radDescription || "",
          cost: r.rwCost,
          rwAmount: r.rwAmount,
          waterVolume: r.waterVolume ?? r.rwWaterVolume,
          usageCount: r.usageCount || 0,
          usedCount: r.usedCount || 0,
          totalGreenmilesSpent: r.totalGreenmilesSpent || 0,
          totalGain: r.totalGain || 0,
        };
      });
  }, [rewards, staysTotalForPeriod, lang]);

  const mapRewardTypeLabelWithLang = (rwType) => mapRewardTypeLabel(rwType, lang);

  return (
    <div className="v2-page">
      <div className="v2-page__header">
        <h1>{translate("v2_rewards_list_title", lang)}</h1>
        <div className="rewards-list__header-right">
          <MonthYearNavigator
            month={selectedMonth}
            year={selectedYear}
            onChange={({ month, year }) => {
              setMonthYear({ month, year });
            }}
          />
        </div>
      </div>
      {hasError && (
        <div className="rewards-list__error">
          <p>{translate("v2_error_load_data", lang)}</p>
        </div>
      )}

      <div className="rewards-list__content">
        <RewardsTopCards
          usageChartData={usageChartData}
          formatCompactNumber={formatCompactNumber}
          formatUsageLegendValue={formatUsageLegendValue}
          statsLoading={statsLoading}
          averageUsageRate={averageUsageRate}
          averageUsageDelta={averageUsageDelta}
        />

        <RewardsAnalysisTable
          loading={loading}
          analysisRows={analysisRows}
          getRewardLogoSrc={getRewardLogoSrc}
          mapRewardTypeBadgeClass={mapRewardTypeBadgeClass}
          mapRewardTypeLabel={mapRewardTypeLabelWithLang}
          getRewardIconColor={getRewardIconColor}
          linkIcon={linkIcon}
          navigate={navigate}
          locationSearch={location.search}
          setSelectedReward={setSelectedReward}
        />

        <RewardsBenefitsTable
          loading={loading}
          analysisRows={analysisRows}
          getRewardLogoSrc={getRewardLogoSrc}
          getRewardIconColor={getRewardIconColor}
          formatCompactNumber={formatCompactNumber}
        />
      </div>

      <RewardsDetailsModal
        selectedReward={selectedReward}
        setSelectedReward={setSelectedReward}
        mapRewardTypeLabel={mapRewardTypeLabelWithLang}
      />
    </div>
  );
};

export default RewardsList;
